import { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { api, API } from "@/lib/api";
import { CustomerSearchSelect } from "@/components/CustomerSearchSelect";
import { parseSendResponse } from "@/lib/sendResponse";
import { downloadPdf } from "@/lib/pdfDownload";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue, SelectGroup, SelectLabel } from "@/components/ui/select";
import { ArrowLeft, Plus, Trash2, Send, Copy, Link as LinkIcon, Briefcase, FileText, Image as ImageIcon, Lock, User, Receipt, Calendar, MapPin, ChevronDown, Ruler } from "lucide-react";
import { toast } from "sonner";
import FileUploader from "@/components/FileUploader";
import ScheduleJobDialog from "@/components/ScheduleJobDialog";
import StreetViewPanel from "@/components/StreetViewPanel";
import LineMeasureDialog from "@/pages/SmartEstimator/LineMeasureDialog";
import ReuseMeasurementDialog from "@/components/ReuseMeasurementDialog";
import RescheduleAppointmentDialog from "@/components/RescheduleAppointmentDialog";
import { inferLineCategory } from "@/lib/measurementCategoryMap";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import EmailComposeDialog from "@/components/EmailComposeDialog";
import EmailThread from "@/components/EmailThread";
import CustomerCommsPanel from "@/components/CustomerCommsPanel";

const EMPTY_LINE = { name: "", description: "", quantity: 1, unit_price: 0, taxable: true, addons: [] };

export default function EstimateEditor() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const nav = useNavigate();
  const isNew = !id || id === "new";

  // Profile-launched flag: when the editor is opened from a customer or lead
  // profile, the customer identity is LOCKED and the picker is hidden.
  const launchedCustomerId = params.get("customer_id") || "";
  const launchedLeadId = params.get("lead_id") || "";
  const [lockedCustomer, setLockedCustomer] = useState(null); // customer doc
  const [lockedLead, setLockedLead] = useState(null);         // lead doc when no linked customer

  const [pickedCustomer, setPickedCustomer] = useState(null);
  const customers = pickedCustomer ? [pickedCustomer] : [];
  const [services, setServices] = useState([]);
  const [form, setForm] = useState({
    customer_id: launchedCustomerId,
    lead_id: launchedLeadId,
    title: "",
    line_items: [{ ...EMPTY_LINE }],
    discount_type: "percent",
    discount_value: 0,
    notes: "",
    terms: "This estimate is valid for 30 days.",
    photos: [],
    expires_at: "",
  });
  const [estimate, setEstimate] = useState(null); // fetched estimate (for existing)
  const [saving, setSaving] = useState(false);
  const [newPhotoUrl, setNewPhotoUrl] = useState("");
  const [scheduleDialogOpen, setScheduleDialogOpen] = useState(false);
  const [rescheduleJob, setRescheduleJob] = useState(null);
  const openRescheduleDialog = async () => {
    if (!estimate?.scheduled_job_id) return;
    try {
      const { data } = await api.get(`/jobs/${estimate.scheduled_job_id}`);
      setRescheduleJob(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load linked job");
    }
  };
  // Smart Estimator embed
  const [calculators, setCalculators] = useState([]);
  const [properties, setProperties] = useState([]);
  const [measurements, setMeasurements] = useState([]);
  const [measureLineIdx, setMeasureLineIdx] = useState(null);
  // Iter57.2 — pre-workspace reuse prompt state.
  const [reusePrompt, setReusePrompt] = useState(null); // { lineIdx, category, address }
  const [initialFocusMeasurementId, setInitialFocusMeasurementId] = useState(null);
  const [serviceCategories, setServiceCategories] = useState([]);

  // True when opened from a customer/lead profile OR when editing an existing
  // estimate (customer is already set and cannot be changed).
  const isProfileLocked = !!(launchedCustomerId || launchedLeadId) && isNew;

  const loadRefs = async () => {
    // When launched from a profile, don't load the entire customer list —
    // the picker is hidden and only the locked identity is needed.
    if (isProfileLocked) {
      const [{ data: sv }] = await Promise.all([api.get("/services")]);
      setServices(sv);
      return;
    }
    const { data: sv } = await api.get("/services");
    setServices(sv);
  };

  const loadLockedIdentity = async () => {
    if (!isProfileLocked) return;
    try {
      if (launchedCustomerId) {
        const { data: c } = await api.get(`/customers/${launchedCustomerId}`);
        setLockedCustomer(c);
        setForm((f) => ({ ...f, customer_id: c.id, lead_id: launchedLeadId || "" }));
      } else if (launchedLeadId) {
        const { data: l } = await api.get(`/leads/${launchedLeadId}`);
        setLockedLead(l);
        // If the lead already has a linked customer, prefer that customer_id
        // so the estimate ties directly to the customer record.
        if (l.customer_id) {
          try {
            const { data: c } = await api.get(`/customers/${l.customer_id}`);
            setLockedCustomer(c);
            setForm((f) => ({ ...f, customer_id: c.id, lead_id: l.id }));
          } catch {
            setForm((f) => ({ ...f, lead_id: l.id }));
          }
        } else {
          setForm((f) => ({ ...f, lead_id: l.id }));
        }
      }
    } catch {
      toast.error("Could not load profile — returning to previous page");
      nav(-1);
    }
  };

  const loadEstimate = async () => {
    if (isNew) return;
    try {
      const { data } = await api.get(`/estimates/${id}`);
      setEstimate(data);
      setForm({
        customer_id: data.customer_id,
        lead_id: data.lead_id || "",
        title: data.title || "",
        line_items: data.line_items && data.line_items.length ? data.line_items : [{ ...EMPTY_LINE }],
        discount_type: data.discount_type || "percent",
        discount_value: data.discount_value || 0,
        notes: data.notes || "",
        terms: data.terms || "",
        photos: data.photos || [],
        expires_at: (data.expires_at || "").slice(0, 10),
      });
      // Also load the customer summary for the read-only header
      if (data.customer_id) {
        try {
          const { data: c } = await api.get(`/customers/${data.customer_id}`);
          setLockedCustomer(c);
        } catch { /* header falls back to id */ }
      }
    } catch { toast.error("Not found"); nav("/app/estimates"); }
  };

  useEffect(() => { loadRefs(); loadEstimate(); loadLockedIdentity(); }, [id]); // eslint-disable-line

  // Load Smart Estimator calculators once (used by every line row).
  useEffect(() => {
    api.get("/estimator/calculators").then(({ data }) => setCalculators(data?.calculators || []))
      .catch(() => setCalculators([]));
    api.get("/service-categories").then(({ data }) => setServiceCategories(data || []))
      .catch(() => setServiceCategories([]));
  }, []);

  // Whenever the active customer changes, refresh the property list. If the
  // customer already has a saved property, adopt the first one; otherwise
  // leave properties empty — we auto-create one on first measurement click.
  const activeCustomerId = form.customer_id || lockedCustomer?.id;
  useEffect(() => {
    if (!activeCustomerId) { setProperties([]); setMeasurements([]); return; }
    api.get(`/estimator/properties?customer_id=${activeCustomerId}`)
      .then(({ data }) => {
        setProperties(data || []);
        const first = (data || [])[0];
        if (first) {
          api.get(`/estimator/properties/${first.id}/measurements`)
            .then(({ data: ms }) => setMeasurements(ms || []))
            .catch(() => setMeasurements([]));
        } else {
          setMeasurements([]);
        }
      })
      .catch(() => setProperties([]));
  }, [activeCustomerId]);

  // Auto-create a property from the customer's address+coord the first time
  // the user opens the map for measurement. Returns the property id.
  const ensureProperty = async () => {
    if (properties.length > 0) return properties[0];
    const cust = lockedCustomer || customers.find((c) => c.id === form.customer_id);
    if (!cust) { toast.error("Pick a customer first"); return null; }
    if (!cust.address) { toast.error("Add an address to this customer first"); return null; }
    try {
      const { data } = await api.post("/estimator/properties", {
        customer_id: cust.id,
        label: "Main property",
        address: cust.address,
        latitude: cust.latitude ?? null,
        longitude: cust.longitude ?? null,
        provider: cust.latitude != null ? "mapbox" : "manual",
      });
      setProperties([data]);
      return data;
    } catch {
      toast.error("Could not create property record");
      return null;
    }
  };

  const openMeasureForLine = async (idx) => {
    const prop = await ensureProperty();
    if (!prop) return;
    // Iter57.2 — if we can infer the line's category, prompt to reuse any
    // existing measurements the property already has for that category
    // before opening a blank workspace.
    const line = form.line_items[idx];
    const category = inferLineCategory(line);
    const address = prop?.address || lockedCustomer?.address || (customers.find(c => c.id === form.customer_id)?.address);
    if (category && address) {
      setReusePrompt({ lineIdx: idx, category, address });
      return;
    }
    setInitialFocusMeasurementId(null);
    setMeasureLineIdx(idx);
  };

  const addLine = () => setForm((f) => ({ ...f, line_items: [...f.line_items, { ...EMPTY_LINE }] }));
  const setLine = (i, patch) => setForm((f) => ({ ...f, line_items: f.line_items.map((li, idx) => idx === i ? { ...li, ...patch } : li) }));
  const rmLine = (i) => setForm((f) => ({ ...f, line_items: f.line_items.filter((_, idx) => idx !== i) }));

  const applyService = (i, serviceId) => {
    const s = services.find((x) => x.id === serviceId);
    if (!s) return;
    // Pre-fill calculator + pricing method from the service's estimator_config
    // when present, so picking a service is one click away from a measurable
    // Smart Estimator line.
    const cfg = s.estimator_config || {};
    const calcKey = cfg.calculator_key || cfg.default_calculator || null;
    const calc = calcKey ? calculators.find((c) => c.key === calcKey) : null;
    const pricingMethod = calc
      ? (calc.input_unit === "ft" ? "linear" : (calc.input_unit === "sqft" ? "area" : "unit"))
      : null;
    const unitLabel = calc
      ? (calc.input_unit === "ft" ? "linear ft" : (calc.input_unit === "sqft" ? "ft²" : "each"))
      : null;
    setLine(i, {
      service_id: s.id,
      name: s.name,
      description: s.description,
      unit_price: s.default_price,
      taxable: s.taxable,
      addons: [],
      calculator_key: calcKey,
      pricing_method: pricingMethod,
      unit_label: unitLabel,
    });
  };

  const addAddon = (i) => setLine(i, { addons: [...(form.line_items[i].addons||[]), { name: "", price: 0, taxable: true }] });
  const setAddon = (i, j, patch) => {
    const line = form.line_items[i];
    const addons = line.addons.map((a, idx) => idx === j ? { ...a, ...patch } : a);
    setLine(i, { addons });
  };
  const rmAddon = (i, j) => setLine(i, { addons: form.line_items[i].addons.filter((_, idx) => idx !== j) });

  const addPhoto = () => {
    if (!newPhotoUrl.trim()) return;
    setForm((f) => ({ ...f, photos: [...(f.photos||[]), newPhotoUrl.trim()] }));
    setNewPhotoUrl("");
  };
  const rmPhoto = (i) => setForm((f) => ({ ...f, photos: f.photos.filter((_, idx) => idx !== i) }));

  const totals = (() => {
    let subtotal = 0;
    let tax = 0;
    const taxRate = estimate?.tax_rate || 0;
    for (const li of form.line_items) {
      const q = Number(li.quantity) || 1;
      const p = Number(li.unit_price) || 0;
      const addonSum = (li.addons||[]).reduce((s, a) => s + (Number(a.price)||0), 0);
      const line = q * p + addonSum;
      subtotal += line;
      if (li.taxable) tax += line * (taxRate/100);
    }
    const discAmt = form.discount_type === "percent" ? subtotal * (Number(form.discount_value)||0)/100 : Number(form.discount_value)||0;
    const afterDisc = Math.max(0, subtotal - discAmt);
    const proTax = subtotal > 0 ? tax * (afterDisc/subtotal) : 0;
    return { subtotal, discount: Math.min(discAmt, subtotal), tax: proTax, total: afterDisc + proTax };
  })();

  const save = async () => {
    // Profile-locked path can save without a picked customer_id — the backend
    // resolves the customer from the lead when needed.
    if (!isProfileLocked && !form.customer_id) { toast.error("Pick a customer"); return; }
    setSaving(true);
    try {
      const payload = {
        customer_id: form.customer_id || null,
        lead_id: form.lead_id || null,
        title: form.title,
        line_items: form.line_items.map((li) => ({...li, quantity: Number(li.quantity)||1, unit_price: Number(li.unit_price)||0, addons: (li.addons||[]).map(a => ({...a, price: Number(a.price)||0}))})),
        discount_type: form.discount_type,
        discount_value: Number(form.discount_value)||0,
        notes: form.notes,
        terms: form.terms,
        photos: form.photos,
        expires_at: form.expires_at || null,
      };
      if (isNew) {
        const { data } = await api.post("/estimates", payload);
        toast.success(`Created ${data.estimate_number}`);
        nav(`/app/estimates/${data.id}`);
      } else {
        const { data } = await api.put(`/estimates/${id}`, payload);
        setEstimate(data);
        toast.success("Saved");
      }
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };

  const [sending, setSending] = useState(false);
  const [composeOpen, setComposeOpen] = useState(false);

  const copyLink = (overrideToken) => {
    const token = overrideToken || estimate?.share_token;
    if (!token) return;
    const link = `${window.location.origin}/estimate/${token}`;
    navigator.clipboard.writeText(link);
    toast.success("Public link copied to clipboard");
  };

  const send = async () => {
    if (!estimate || sending) return;
    if (!window.confirm(`Send estimate ${estimate.estimate_number} to the customer's email?`)) return;
    setSending(true);
    let status;
    let data;
    try {
      const resp = await api.post(`/estimates/${id}/send`, { channel: "email" });
      status = resp.status; data = resp.data;
    } catch (e) {
      status = e?.response?.status; data = e?.response?.data;
    }
    const outcome = parseSendResponse({ status, data, kind: "estimate" });
    if (outcome.kind === "success") {
      if (outcome.document) setEstimate(outcome.document);
      toast.success(
        <div data-testid="estimate-send-success-toast">
          Emailed to customer{outcome.correlationId ? ` · ref ${outcome.correlationId}` : ""}
        </div>
      );
    } else {
      showSendErrorToast(outcome);
    }
    setSending(false);
  };

  const showSendErrorToast = (outcome) => {
    // Iter55 — accept the parsed SendOutcome directly and pick a distinct
    // testid per kind so frontend tests can assert branch selection.
    const testid = ({
      provider_failure: "send-error-toast",
      in_progress: "send-inprogress-toast",
      already_sent: "send-alreadysent-toast",
      validation: "send-validation-toast",
      unknown: "send-error-toast",
    })[outcome.kind] || "send-error-toast";
    const publicUrl = outcome.publicUrl || (estimate?.share_token
      ? `${window.location.origin}/estimate/${estimate.share_token}`
      : null);
    toast.error(
      <div className="space-y-2" data-testid={testid}>
        <div className="text-sm">
          {outcome.message}
          {outcome.correlationId ? ` (ref ${outcome.correlationId})` : ""}
          {outcome.priorCorrelationId ? ` · prior ${outcome.priorCorrelationId}` : ""}
        </div>
        <div className="flex gap-2">
          {publicUrl && (
            <button
              onClick={() => { navigator.clipboard.writeText(publicUrl); toast.success("Public link copied"); }}
              className="text-xs px-2 py-1 rounded bg-white text-zinc-900 border border-zinc-300"
              data-testid="send-error-copy-link"
            >Copy public link</button>
          )}
          {outcome.canRetry && (
            <button
              onClick={send}
              className="text-xs px-2 py-1 rounded bg-white text-zinc-900 border border-zinc-300"
              data-testid="send-error-retry-btn"
            >Retry</button>
          )}
        </div>
      </div>
    );
  };

  const downloadEstimatePdf = async () => {
    if (!estimate) return;
    const url = `${API}/estimates/${id}/pdf?auth=${encodeURIComponent(localStorage.getItem("lf_token") || "")}`;
    const res = await downloadPdf(url, `estimate-${estimate.estimate_number || id}.pdf`);
    if (!res.ok) {
      toast.error(
        <div data-testid={`estimate-pdf-${res.kind}-toast`}>
          {res.message}
          <div className="mt-1">
            <button
              onClick={downloadEstimatePdf}
              className="text-xs px-2 py-1 rounded bg-white text-zinc-900 border border-zinc-300"
              data-testid="estimate-pdf-retry-btn"
            >Retry</button>
          </div>
        </div>
      );
    }
  };

  const convertToJob = async () => {
    // Iter32 — the old button silently created an unscheduled job. The new
    // flow opens ScheduleJobDialog so the user can pick a date + tech in
    // the same modal. If a job already exists we just navigate to it
    // (idempotent, prevents accidental duplicates).
    if (estimate?.scheduled_job_id) {
      nav(`/app/jobs/${estimate.scheduled_job_id}`);
      return;
    }
    setScheduleDialogOpen(true);
  };

  const createInvoice = async () => {
    // Idempotency: if the estimate is already linked to an invoice,
    // just navigate to it instead of creating a duplicate.
    if (estimate?.converted_to_invoice_id) {
      nav(`/app/invoices/${estimate.converted_to_invoice_id}`);
      return;
    }
    if (!window.confirm(
      `Create an invoice from ${estimate?.estimate_number}?\n\n` +
      `The estimate stays in place for your records. The new invoice will use your Invoice template and get its own INV-#### number.`
    )) return;
    try {
      const { data } = await api.post(`/estimates/${id}/create-invoice`);
      toast.success(`Invoice ${data.invoice_number} created`);
      // Update local estimate so the button flips to "View invoice"
      setEstimate(prev => prev ? { ...prev, converted_to_invoice_id: data.id, converted_at: data.created_at } : prev);
      nav(`/app/invoices/${data.id}`);
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail || e?.message || "Failed";
      toast.error(`Convert failed${status ? ` (${status})` : ""}: ${detail}`);
    }
  };

  const readOnly = estimate && (estimate.status === "accepted" || estimate.status === "declined");

  return (
    <div className="p-8 max-w-6xl">
      <button onClick={()=>nav("/app/estimates")} className="text-sm text-zinc-500 hover:text-zinc-900 flex items-center gap-1 mb-4">
        <ArrowLeft className="w-4 h-4"/> All estimates
      </button>

      <div className="flex items-start justify-between gap-4 mb-6 flex-wrap">
        <div>
          <div className="text-xs font-mono-accent uppercase tracking-widest text-[#FF5A00] mb-1">
            {estimate ? estimate.estimate_number : "New estimate"}
          </div>
          <h1 className="font-heading text-4xl font-bold">{isNew ? "New estimate" : estimate?.title || "Estimate"}</h1>
          {estimate && (
            <div className="mt-2 flex items-center gap-2 text-xs">
              <span className="capitalize px-2 py-0.5 border rounded bg-zinc-100">{estimate.status}</span>
              {estimate.sent_at && <span className="text-zinc-500">Sent {new Date(estimate.sent_at).toLocaleString()}</span>}
              {estimate.viewed_at && <span className="text-amber-700">Viewed {new Date(estimate.viewed_at).toLocaleString()}</span>}
              {estimate.accepted_at && <span className="text-emerald-700">Accepted {new Date(estimate.accepted_at).toLocaleString()}{estimate.signature_name ? ` · signed ${estimate.signature_name}` : ""}</span>}
              {estimate.declined_at && <span className="text-rose-700">Declined{estimate.decline_reason ? `: ${estimate.decline_reason}` : ""}</span>}
            </div>
          )}
        </div>
        <div className="flex flex-wrap gap-2">
          {estimate && (
            <>
              <Button variant="outline" onClick={copyLink} data-testid="estimate-copy-link-btn"><LinkIcon className="w-4 h-4 mr-1"/> Copy link</Button>
              <Button variant="outline" onClick={downloadEstimatePdf} data-testid="estimate-pdf-btn"><FileText className="w-4 h-4 mr-1"/> PDF</Button>
              {estimate.status !== "accepted" && estimate.status !== "declined" && (
                <Button variant="outline" onClick={send} disabled={sending} data-testid="estimate-send-btn"><Send className="w-4 h-4 mr-1"/> {sending ? "Sending…" : "Send by email"}</Button>
              )}
              <CustomerCommsPanel
                customerId={lockedCustomer?.id || form.customer_id}
                customer={lockedCustomer}
                estimateId={id}
                triggerLabel="Open SMS thread"
                testId="estimate-open-sms-btn"
              />
              {lockedCustomer?.email && (
                <Button variant="outline" onClick={() => setComposeOpen(true)} className="border-sky-200 text-sky-700 hover:bg-sky-50" data-testid="estimate-compose-email-btn">
                  <FileText className="w-4 h-4 mr-1"/> Compose email
                </Button>
              )}
              {estimate.status === "accepted" && !estimate.scheduled_job_id && (
                <Button onClick={convertToJob} className="bg-emerald-600 hover:bg-emerald-700 text-white btn-lift" data-testid="estimate-convert-btn"><Briefcase className="w-4 h-4 mr-1"/> Convert to job</Button>
              )}
              {/* Iter32 — Schedule Job button is always visible so users can
                  schedule from any non-declined estimate. When a job is
                  already linked, the button flips to "View job" and shows
                  the scheduled date so accidental duplicates are impossible. */}
              {estimate.status !== "declined" && (
                estimate.scheduled_job_id ? (
                  <>
                    <Button
                      variant="outline"
                      onClick={()=>nav(`/app/jobs/${estimate.scheduled_job_id}`)}
                      className="border-blue-600 text-blue-700 hover:bg-blue-50"
                      data-testid="estimate-view-job-btn"
                      title={estimate.scheduled_job_at ? `Scheduled for ${new Date(estimate.scheduled_job_at).toLocaleString(undefined, { timeZone: "America/Chicago" })} CT` : "View scheduled job"}
                    >
                      <Calendar className="w-4 h-4 mr-1"/>
                      View job{estimate.scheduled_job_at ? ` · ${new Date(estimate.scheduled_job_at).toLocaleDateString(undefined, { timeZone: "America/Chicago" })}` : ""}
                    </Button>
                    <Button
                      variant="outline"
                      onClick={openRescheduleDialog}
                      className="border-emerald-600 text-emerald-700 hover:bg-emerald-50"
                      data-testid="estimate-reschedule-btn"
                      title="Reschedule the linked appointment"
                    >
                      <Calendar className="w-4 h-4 mr-1"/>
                      Reschedule appointment
                    </Button>
                  </>
                ) : (
                  <Button
                    onClick={()=>setScheduleDialogOpen(true)}
                    className="bg-blue-600 hover:bg-blue-700 text-white btn-lift"
                    data-testid="estimate-schedule-job-btn"
                  >
                    <Calendar className="w-4 h-4 mr-1"/> Schedule Job
                  </Button>
                )
              )}
              {/* Convert-to-invoice is available for any saved estimate. If
                  an invoice already exists, the button flips to a
                  navigational "View invoice" to prevent duplicates. */}
              {estimate.converted_to_invoice_id ? (
                <Button
                  variant="outline"
                  onClick={()=>nav(`/app/invoices/${estimate.converted_to_invoice_id}`)}
                  className="border-emerald-600 text-emerald-700 hover:bg-emerald-50"
                  data-testid="estimate-view-invoice-btn"
                  title="An invoice was already created from this estimate"
                >
                  <Receipt className="w-4 h-4 mr-1"/> View invoice {" "}
                </Button>
              ) : (
                <Button
                  onClick={createInvoice}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white btn-lift"
                  data-testid="estimate-create-invoice-btn"
                >
                  <Receipt className="w-4 h-4 mr-1"/> Convert to invoice
                </Button>
              )}
            </>
          )}
          {!readOnly && (
            <Button onClick={save} disabled={saving} className="bg-[#FF5A00] hover:bg-[#E04F00] text-white btn-lift" data-testid="estimate-save-btn">
              {saving ? "Saving…" : isNew ? "Create estimate" : "Save changes"}
            </Button>
          )}
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          {/* Customer + title */}
          <div className="bg-white border border-zinc-200 rounded-xl p-6 card-crisp space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Customer</Label>
                {(isProfileLocked || !isNew) ? (
                  <LockedCustomerCard
                    customer={lockedCustomer}
                    lead={lockedLead}
                    origin={launchedLeadId ? "lead" : "customer"}
                  />
                ) : (
                  <CustomerSearchSelect
                    selected={pickedCustomer}
                    onSelect={(c)=>{ setPickedCustomer(c); setForm((f)=>({...f, customer_id: c.id})); }}
                    disabled={readOnly}
                    testid="estimate-customer-select"
                  />
                )}
              </div>
              <div>
                <Label>Title</Label>
                <Input value={form.title} onChange={(e)=>setForm({...form, title: e.target.value})} placeholder="e.g. Cedar Lane roof replacement" disabled={readOnly} data-testid="estimate-title-input"/>
              </div>
            </div>
          </div>

          {/* Line items */}
          <div className="bg-white border border-zinc-200 rounded-xl p-6 card-crisp">
            <div className="flex items-center justify-between mb-1">
              <h2 className="font-heading text-xl font-bold flex items-center gap-2"><FileText className="w-5 h-5 text-[#FF5A00]"/> Line items</h2>
              {!readOnly && <Button variant="outline" size="sm" onClick={addLine} data-testid="estimate-add-line-btn"><Plus className="w-4 h-4 mr-1"/> Line</Button>}
            </div>
            <div className="flex items-center gap-2 mb-4 text-[11px] text-zinc-500 font-mono-accent uppercase tracking-widest">
              <Ruler className="w-3 h-3 text-[#FF5A00]" />
              Smart Estimator embedded — pick a calculator, click <MapPin className="w-3 h-3 inline"/> to measure on the map, or the ▾ to pick a saved measurement.
            </div>
            <div className="space-y-4">
              {form.line_items.map((li, i) => {
                const calc = calculators.find((c) => c.key === li.calculator_key);
                const isMeasurable = calc && (calc.input_unit === "sqft" || calc.input_unit === "ft");
                const qtyLabel = calc?.input_unit === "ft"
                  ? "Length (ft)"
                  : calc?.input_unit === "sqft" ? "Area (ft²)" : "Qty";
                const unitLabel = calc?.input_unit === "ft" ? "linear ft" : (calc?.input_unit === "sqft" ? "ft²" : "");
                const q = Number(li.quantity) || 0;
                const p = Number(li.unit_price) || 0;
                const lineTotal = q * p + (li.addons || []).reduce((s, a) => s + (Number(a.price) || 0), 0);
                const compatibleSaved = measurements.filter((m) => !calc || (calculators.find((c) => c.key === m.calculator_key)?.input_unit === calc.input_unit));
                const linkedMeasurement = li.measurement_id
                  ? measurements.find((m) => m.id === li.measurement_id)
                  : null;
                return (
                <div key={i} className="rounded-lg border border-zinc-200 p-4 space-y-2" data-testid={`estimate-line-${i}`}>
                  <div className="grid grid-cols-12 gap-2">
                    <div className="col-span-4">
                      <Label className="text-[10px] uppercase tracking-widest text-zinc-500">Service</Label>
                      <Select value={li.service_id||"custom"} onValueChange={(v)=>v==="custom"?setLine(i,{service_id:null}):applyService(i,v)} disabled={readOnly}>
                        <SelectTrigger><SelectValue placeholder="Pick or custom"/></SelectTrigger>
                        <SelectContent className="max-h-96">
                          <SelectItem value="custom">Custom line</SelectItem>
                          {/* Grouped by category — Iter41. Uncategorized
                              services fall into a trailing group so nothing
                              disappears from the picker during migration. */}
                          {serviceCategories.map((c) => {
                            const catSvcs = services.filter((s) => s.service_category_id === c.id && s.active !== false);
                            if (catSvcs.length === 0) return null;
                            return (
                              <SelectGroup key={c.id}>
                                <SelectLabel>{c.name}</SelectLabel>
                                {catSvcs.map((s) => (
                                  <SelectItem key={s.id} value={s.id}>
                                    {s.name}{s.default_price != null ? ` · $${s.default_price}` : ""}
                                  </SelectItem>
                                ))}
                              </SelectGroup>
                            );
                          })}
                          {(() => {
                            const uncat = services.filter((s) => !s.service_category_id && s.active !== false);
                            if (uncat.length === 0) return null;
                            return (
                              <SelectGroup>
                                <SelectLabel>Uncategorized</SelectLabel>
                                {uncat.map((s) => (
                                  <SelectItem key={s.id} value={s.id}>
                                    {s.name}{s.default_price != null ? ` · $${s.default_price}` : ""}
                                  </SelectItem>
                                ))}
                              </SelectGroup>
                            );
                          })()}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="col-span-3">
                      <Label className="text-[10px] uppercase tracking-widest text-zinc-500">Name</Label>
                      <Input value={li.name} onChange={(e)=>setLine(i, {name: e.target.value})} disabled={readOnly}/>
                    </div>
                    <div className="col-span-3">
                      <Label className="text-[10px] uppercase tracking-widest text-zinc-500">Calculator</Label>
                      <Select
                        value={li.calculator_key || "custom"}
                        onValueChange={(v) => {
                          const patch = v === "custom" ? { calculator_key: null, pricing_method: null, unit_label: null } : {
                            calculator_key: v,
                            pricing_method: (calculators.find((c) => c.key === v)?.input_unit === "ft") ? "linear" : (calculators.find((c) => c.key === v)?.input_unit === "sqft" ? "area" : "unit"),
                            unit_label: calculators.find((c) => c.key === v)?.input_unit === "ft" ? "linear ft" : (calculators.find((c) => c.key === v)?.input_unit === "sqft" ? "ft²" : "each"),
                          };
                          setLine(i, patch);
                        }}
                        disabled={readOnly}
                      >
                        <SelectTrigger className="h-9 text-xs" data-testid={`estimate-line-calc-${i}`}><SelectValue placeholder="Custom" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="custom">Custom (no calc)</SelectItem>
                          {calculators.map((c) => <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="col-span-2">
                      <Label className="text-[10px] uppercase tracking-widest text-zinc-500">{qtyLabel}</Label>
                      <div className="flex items-center gap-1">
                        <Input
                          type="number" step="0.5"
                          value={li.quantity}
                          onChange={(e)=>setLine(i, {quantity: e.target.value})}
                          disabled={readOnly}
                          data-testid={`estimate-line-qty-${i}`}
                        />
                        {isMeasurable && !readOnly && (
                          <>
                            <button
                              type="button"
                              onClick={() => openMeasureForLine(i)}
                              className={`h-9 w-9 shrink-0 flex items-center justify-center rounded border ${linkedMeasurement ? "bg-orange-50 border-[#FF5A00] text-[#FF5A00]" : "border-zinc-300 text-zinc-600 hover:border-[#FF5A00] hover:text-[#FF5A00]"}`}
                              title={linkedMeasurement ? "Edit map measurement" : "Measure on map"}
                              data-testid={`estimate-line-map-btn-${i}`}
                            >
                              <MapPin className="w-4 h-4" />
                            </button>
                            <Popover>
                              <PopoverTrigger asChild>
                                <button
                                  type="button"
                                  className="h-9 w-6 shrink-0 flex items-center justify-center rounded border border-zinc-300 text-zinc-600 hover:border-[#FF5A00] hover:text-[#FF5A00]"
                                  title="Pick a saved measurement"
                                  data-testid={`estimate-line-saved-btn-${i}`}
                                >
                                  <ChevronDown className="w-3.5 h-3.5" />
                                </button>
                              </PopoverTrigger>
                              <PopoverContent className="w-72 p-2" align="end">
                                <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1">Saved measurements</div>
                                {compatibleSaved.length === 0 && (
                                  <div className="text-xs text-zinc-500 p-2">No compatible measurements saved yet.</div>
                                )}
                                <div className="max-h-56 overflow-y-auto">
                                  {compatibleSaved.map((m) => (
                                    <button
                                      key={m.id}
                                      type="button"
                                      onClick={() => setLine(i, {
                                        measurement_id: m.id,
                                        quantity: m.output_value,
                                        calculator_key: m.calculator_key || li.calculator_key,
                                        unit_label: m.output_unit || m.input_unit || li.unit_label,
                                      })}
                                      className={`w-full text-left px-2 py-1.5 rounded hover:bg-zinc-100 ${li.measurement_id === m.id ? "bg-orange-50" : ""}`}
                                      data-testid={`estimate-line-saved-item-${i}-${m.id}`}
                                    >
                                      <div className="text-sm truncate">{m.label || m.calculator_key}</div>
                                      <div className="text-[10px] text-zinc-500 font-mono-accent">
                                        {Number(m.output_value || 0).toLocaleString()} {m.output_unit || m.input_unit}
                                      </div>
                                    </button>
                                  ))}
                                </div>
                              </PopoverContent>
                            </Popover>
                          </>
                        )}
                      </div>
                    </div>
                    <div className="col-span-2">
                      <Label className="text-[10px] uppercase tracking-widest text-zinc-500">Unit price</Label>
                      <Input type="number" step="0.01" value={li.unit_price} onChange={(e)=>setLine(i, {unit_price: e.target.value})} disabled={readOnly}/>
                    </div>
                    <div className="col-span-12 flex items-center justify-between pt-1 gap-2 flex-wrap text-xs text-zinc-600">
                      <div className="flex items-center gap-3 flex-wrap">
                        <label className="flex items-center gap-1">
                          <input type="checkbox" checked={!!li.taxable} onChange={(e)=>setLine(i, {taxable: e.target.checked})} disabled={readOnly}/> Taxable
                        </label>
                        {li.calculator_key && (
                          <span className="font-mono-accent" data-testid={`estimate-line-formula-${i}`}>
                            {q.toLocaleString()} {unitLabel || "×"} × ${p.toFixed(2)} = <strong className="text-zinc-900">${lineTotal.toFixed(2)}</strong>
                          </span>
                        )}
                        {linkedMeasurement && (
                          <span className="text-[#FF5A00] flex items-center gap-1" data-testid={`estimate-line-linked-${i}`}>
                            <MapPin className="w-3 h-3" /> {linkedMeasurement.label || "measurement"}
                            {!readOnly && (
                              <button type="button" className="ml-1 text-zinc-400 hover:text-red-600" onClick={() => setLine(i, { measurement_id: null })} title="Unlink (keeps quantity)">×</button>
                            )}
                          </span>
                        )}
                      </div>
                      {!readOnly && (
                        <div className="flex items-center gap-3">
                          <button type="button" onClick={()=>addAddon(i)} className="text-[#FF5A00] hover:underline">+ Add add-on</button>
                          <button type="button" onClick={()=>rmLine(i)} className="text-rose-600 hover:underline flex items-center gap-1"><Trash2 className="w-3 h-3"/> Remove</button>
                        </div>
                      )}
                    </div>
                  </div>
                  {(li.addons||[]).map((a, j) => (
                    <div key={j} className="grid grid-cols-12 gap-2 pl-4 border-l-2 border-orange-200 py-1">
                      <div className="col-span-8">
                        <Input placeholder="Add-on name" value={a.name} onChange={(e)=>setAddon(i, j, {name: e.target.value})} disabled={readOnly}/>
                      </div>
                      <div className="col-span-3">
                        <Input type="number" step="0.01" placeholder="Price" value={a.price} onChange={(e)=>setAddon(i, j, {price: e.target.value})} disabled={readOnly}/>
                      </div>
                      {!readOnly && (
                        <div className="col-span-1 flex items-center justify-end">
                          <button type="button" onClick={()=>rmAddon(i, j)} className="text-zinc-400 hover:text-rose-600"><Trash2 className="w-4 h-4"/></button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )})}
            </div>
          </div>

          {/* Notes + terms + photos */}
          <div className="bg-white border border-zinc-200 rounded-xl p-6 card-crisp space-y-4">
            <div>
              <Label>Notes to customer</Label>
              <Textarea rows={2} value={form.notes} onChange={(e)=>setForm({...form, notes: e.target.value})} disabled={readOnly} data-testid="estimate-notes-input"/>
            </div>
            <div>
              <Label>Terms & conditions</Label>
              <Textarea rows={3} value={form.terms} onChange={(e)=>setForm({...form, terms: e.target.value})} disabled={readOnly} data-testid="estimate-terms-input"/>
            </div>
            <div>
              <Label className="flex items-center gap-1"><ImageIcon className="w-3.5 h-3.5"/> Photos (URLs)</Label>
              {!readOnly && (
                <div className="flex gap-2 mb-2">
                  <Input value={newPhotoUrl} onChange={(e)=>setNewPhotoUrl(e.target.value)} placeholder="Paste an image URL" data-testid="estimate-photo-input"/>
                  <Button type="button" variant="outline" onClick={addPhoto} data-testid="estimate-add-photo-btn">Add</Button>
                </div>
              )}
              {form.photos && form.photos.length > 0 && (
                <div className="grid grid-cols-4 gap-2">
                  {form.photos.map((p, i) => (
                    <div key={i} className="relative aspect-square rounded-lg overflow-hidden border border-zinc-200 group">
                      <img src={p} alt="" className="w-full h-full object-cover"/>
                      {!readOnly && (
                        <button type="button" onClick={()=>rmPhoto(i)} className="absolute top-1 right-1 bg-black/60 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity">
                          <Trash2 className="w-3 h-3"/>
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
            {id && (
              <div className="pt-2">
                <FileUploader resourceType="estimate" resourceId={id} title="Uploaded files (photos, PDFs)" canEdit={!readOnly}/>
              </div>
            )}
          </div>
        </div>

        {/* Right: totals */}
        <div className="space-y-4">
          {/* Street View — same component used on Customer/Lead profiles.
              Uses the linked customer's saved coord when present so no
              re-geocode is needed and Mapbox measurements + Google Street
              View share the exact same point. Falls back gracefully when
              coverage is missing or the address is empty. */}
          {(() => {
            const activeCust = lockedCustomer || customers.find((c) => c.id === form.customer_id);
            const svAddress = activeCust?.address || estimate?.service_address || lockedLead?.property_address || "";
            const svCoord = (activeCust && activeCust.latitude != null && activeCust.longitude != null)
              ? { lat: activeCust.latitude, lng: activeCust.longitude }
              : (lockedLead && lockedLead.latitude != null && lockedLead.longitude != null
                  ? { lat: lockedLead.latitude, lng: lockedLead.longitude }
                  : null);
            return (
              <StreetViewPanel
                address={svAddress}
                coord={svCoord}
                title="Street View"
              />
            );
          })()}
          <div className="bg-white border border-zinc-200 rounded-xl p-6 card-crisp sticky top-4">
            <h2 className="font-heading text-xl font-bold mb-4">Totals</h2>
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Discount type</Label>
                  <Select value={form.discount_type} onValueChange={(v)=>setForm({...form, discount_type: v})} disabled={readOnly}>
                    <SelectTrigger><SelectValue/></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="percent">% Percent</SelectItem>
                      <SelectItem value="fixed">$ Fixed</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Discount value</Label>
                  <Input type="number" step="0.01" value={form.discount_value} onChange={(e)=>setForm({...form, discount_value: e.target.value})} disabled={readOnly} data-testid="estimate-discount-input"/>
                </div>
              </div>
              <div>
                <Label className="text-xs">Expires on</Label>
                <Input type="date" value={form.expires_at} onChange={(e)=>setForm({...form, expires_at: e.target.value})} disabled={readOnly}/>
              </div>
            </div>

            <div className="border-t border-zinc-200 mt-4 pt-4 space-y-2 text-sm">
              <Row label="Subtotal" value={`$${totals.subtotal.toFixed(2)}`}/>
              <Row label="Discount" value={`- $${totals.discount.toFixed(2)}`} muted/>
              <Row label={`Tax${estimate?.tax_rate ? ` (${estimate.tax_rate}%)` : ""}`} value={`$${totals.tax.toFixed(2)}`} muted/>
              <div className="border-t border-zinc-200 pt-3 flex items-center justify-between">
                <span className="font-heading font-bold">Total</span>
                <span className="font-heading text-3xl font-black text-[#FF5A00]" data-testid="estimate-total">${totals.total.toFixed(2)}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Phase 2 — Email history (scoped to the customer so estimate + customer emails co-exist) */}
      {(lockedCustomer?.id || form.customer_id) && (
        <div className="mt-6">
          <EmailThread
            customerId={lockedCustomer?.id || form.customer_id}
            estimateId={id}
            prefillTo={lockedCustomer?.email || ""}
            title="Email history"
          />
        </div>
      )}
      <EmailComposeDialog
        open={composeOpen}
        onClose={() => setComposeOpen(false)}
        customerId={lockedCustomer?.id || form.customer_id}
        estimateId={id}
        prefillTo={lockedCustomer?.email || ""}
      />
      <ScheduleJobDialog
        open={scheduleDialogOpen}
        onClose={()=>setScheduleDialogOpen(false)}
        estimate={estimate ? { ...estimate, service_address: lockedCustomer?.address || estimate.service_address, customer_name: estimate.customer_name || lockedCustomer?.name } : null}
        onScheduled={(job) => {
          setEstimate(prev => prev ? { ...prev, scheduled_job_id: job.id, scheduled_job_at: job.scheduled_start } : prev);
          nav(`/app/jobs/${job.id}`);
        }}
      />
      <RescheduleAppointmentDialog
        open={!!rescheduleJob}
        onOpenChange={(v)=>!v && setRescheduleJob(null)}
        job={rescheduleJob}
        onRescheduled={(payload)=>{
          setEstimate(prev => prev ? { ...prev, scheduled_job_at: payload.scheduled_start } : prev);
          setRescheduleJob(null);
        }}
      />
      <LineMeasureDialog
        open={measureLineIdx !== null}
        onClose={() => setMeasureLineIdx(null)}
        propertyId={properties[0]?.id}
        propertyAddress={properties[0]?.address || lockedCustomer?.address || (customers.find(c => c.id === form.customer_id)?.address)}
        calculatorKey={measureLineIdx !== null ? form.line_items[measureLineIdx]?.calculator_key : null}
        calculators={calculators}
        defaultLabel={measureLineIdx !== null ? (form.line_items[measureLineIdx]?.name || "") : ""}
        existingMeasurement={
          measureLineIdx !== null && form.line_items[measureLineIdx]?.measurement_id
            ? measurements.find((m) => m.id === form.line_items[measureLineIdx].measurement_id)
            : null
        }
        // Iter D.1 — estimate context so the Google workspace can link
        // the selected measurement(s) to the target line via the backend.
        estimateId={!isNew && id ? id : (estimate?.id || null)}
        estimateLineItems={form.line_items}
        targetLineItemIndex={measureLineIdx}
        initialFocusMeasurementId={initialFocusMeasurementId}
        onApply={({ measurement_id, output_value, measurement, estimate: linkedEstimate }) => {
          if (measureLineIdx === null) return;
          const calc = calculators.find((c) => c.key === form.line_items[measureLineIdx].calculator_key);
          const unit_label = calc?.input_unit === "ft" ? "linear ft" : (calc?.input_unit === "sqft" ? "ft²" : (measurement?.output_unit || "each"));
          setLine(measureLineIdx, {
            measurement_id,
            quantity: output_value,
            unit_label,
          });
          // If the backend returned the refreshed estimate (D.1 link path),
          // also apply the quantity from the authoritative response so all
          // linked lines refresh — not just the invoking one.
          if (linkedEstimate?.line_items) {
            setForm((f) => ({
              ...f,
              line_items: f.line_items.map((li, idx) => {
                const upstream = linkedEstimate.line_items[idx];
                return upstream ? { ...li, quantity: upstream.quantity, measurement_id: upstream.measurement_id || li.measurement_id } : li;
              }),
            }));
          }
          setMeasurements((xs) => {
            const others = xs.filter((x) => x.id !== measurement.id);
            return [measurement, ...others];
          });
          toast.success(`Applied ${output_value} ${unit_label} to line`);
        }}
      />

      {/* Iter57.2 — reuse existing measurement pre-prompt (mounted here so
          state changes in openMeasureForLine actually surface UI). */}
      <ReuseMeasurementDialog
        open={!!reusePrompt}
        propertyAddress={reusePrompt?.address}
        serviceCategory={reusePrompt?.category}
        onCancel={() => setReusePrompt(null)}
        onReuse={(m) => {
          const idx = reusePrompt?.lineIdx;
          setReusePrompt(null);
          setInitialFocusMeasurementId(m.id);
          setMeasureLineIdx(idx);
        }}
        onCreateNew={() => {
          const idx = reusePrompt?.lineIdx;
          setReusePrompt(null);
          setInitialFocusMeasurementId(null);
          setMeasureLineIdx(idx);
        }}
      />
    </div>
  );
}

function Row({ label, value, muted }) {
  return (
    <div className="flex items-center justify-between">
      <span className={muted?"text-zinc-500":"text-zinc-700"}>{label}</span>
      <span className={`font-mono-accent ${muted?"text-zinc-600":"text-zinc-900 font-medium"}`}>{value}</span>
    </div>
  );
}

// ============================================================
// Locked customer card — shown when the editor is launched from a
// customer/lead profile OR when viewing an existing estimate. The customer
// identity is displayed read-only and cannot be changed. This is a hard
// guarantee: no <Select>, no editable ID field, no cross-customer swap.
// ============================================================
function LockedCustomerCard({ customer, lead, origin }) {
  const c = customer;
  // Derive display values from the customer or (fallback) from the lead
  const name = c?.name || lead?.customer_name || "Customer";
  const email = c?.email || lead?.email || "";
  const phone = c?.phone || lead?.phone || "";
  const address = c?.address || lead?.service_address || "";
  return (
    <div
      className="border border-zinc-200 rounded-lg bg-zinc-50 p-3 text-sm"
      data-testid="estimate-customer-locked"
      data-customer-id={c?.id || ""}
      data-lead-id={lead?.id || ""}
      data-origin={origin}
    >
      <div className="flex items-center gap-2 mb-1">
        <User className="w-4 h-4 text-[#FF5A00]"/>
        <div className="font-medium truncate" data-testid="estimate-customer-locked-name">{name}</div>
        <span
          className="ml-auto text-[10px] uppercase tracking-widest font-mono-accent text-zinc-500 flex items-center gap-1"
          data-testid="estimate-customer-locked-badge"
        >
          <Lock className="w-3 h-3"/> Locked · From {origin}
        </span>
      </div>
      {(email || phone) && (
        <div className="text-xs text-zinc-600 truncate">
          {[email, phone].filter(Boolean).join(" · ")}
        </div>
      )}
      {address && (
        <div className="text-xs text-zinc-500 truncate" data-testid="estimate-customer-locked-address">{address}</div>
      )}
    </div>
  );
}
